import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
} from "@mui/material";
import {
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import React, { useEffect, useState } from "react";
import CustomSelectFilter from "../../common/CustomSelectFilter";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  setCommunicationInteraction,
  setEndTime,
  setLogTypeValue,
  setNotesValue,
  setProgramKeyValue,
  setStartTime,
  timeChangedFunc,
} from "../../actions/AppConfig";
import {
  formatElapsedTime,
  isFutureLogDateTime,
  logValueOptions,
} from "../../utils/helpers";
import dayjs from "dayjs";

const dayjsToTimeOption = (value) => ({
  hours: dayjs(value).format("hh"),
  minutes: dayjs(value).format("mm"),
  amPm: dayjs(value).format("A"),
});

const EndTimeModalContent = ({
  endTimeError,
  setEndTimeError,
  startFutureTimeError,
  endFutureTimeError,
  setStartFutureTimeError,
  setEndFutureTimeError,
  openModal,
  programOptions,
}) => {
  const dispatch = useDispatch();
  const timerModal = useSelector((state) => state.timerModal);
  const [openStartTimePicker, setOpenStartTimePicker] = useState(false);
  const [openEndTimePicker, setOpenEndTimePicker] = useState(false);

  const {
    startTimeValue,
    elapsedTime,
    endTimeValue,
    logTypeValue,
    programKeyValue,
    notesValue,
    isPaused,
    communicationInteraction,
    timeChanged,
  } = timerModal;

  const updateEndTimeIfNeeded = (dispatch, endTimeValue) => {
    const now = dayjs().set("second", 0).set("millisecond", 0);
    const endTime = dayjs(endTimeValue).set("second", 0).set("millisecond", 0);

    if (now.minute() !== endTime.minute()) {
      dispatch(setEndTime(now));
    }
  };

  useEffect(() => {
    let animationFrameId;

    const checkTimeUpdate = () => {
      animationFrameId = requestAnimationFrame(checkTimeUpdate);
      updateEndTimeIfNeeded(dispatch, endTimeValue);
    };

    if (openModal && !timeChanged) {
      animationFrameId = requestAnimationFrame(checkTimeUpdate);
    }

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [openModal, timeChanged, endTimeValue, dispatch]);

  useEffect(() => {
    if (!openModal) return;

    const today = dayjs();

    if (startTimeValue) {
      setStartFutureTimeError(
        isFutureLogDateTime(today, dayjsToTimeOption(startTimeValue))
      );
    }
    if (endTimeValue) {
      setEndFutureTimeError(
        isFutureLogDateTime(today, dayjsToTimeOption(endTimeValue))
      );
    }
  }, [
    openModal,
    startTimeValue,
    endTimeValue,
    setStartFutureTimeError,
    setEndFutureTimeError,
  ]);

  useEffect(() => {
    if (!openModal || programKeyValue || !programOptions?.length) return;

    const rtmProgram = programOptions.find((option) => option.value === "RTM");
    if (rtmProgram) {
      dispatch(setProgramKeyValue(rtmProgram));
    }
  }, [openModal, programOptions, programKeyValue, dispatch]);

  return (
    <Stack gap="21px">
      {!isPaused && (
        <Alert severity="info">
          The timer is active. {formatElapsedTime(elapsedTime, false)}
        </Alert>
      )}

      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        gap="24px"
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            label="Start Time"
            value={startTimeValue}
            onChange={(newValue) => {
              setEndTimeError(false);
              dispatch(setStartTime(newValue));
              dispatch(timeChangedFunc(true));
            }}
            onOpen={() => setOpenStartTimePicker(true)}
            onClose={() => setOpenStartTimePicker(false)}
            open={openStartTimePicker}
            slotProps={{
              textField: {
                fullWidth: true,
                error: startFutureTimeError,
                helperText: startFutureTimeError
                  ? "Start time cannot be in the future."
                  : "",
                sx: {
                  "& .MuiFormHelperText-root": {
                    marginLeft: 0,
                    marginRight: 0,
                  },
                  ...(startFutureTimeError && {
                    "& .MuiFormHelperText-root.Mui-error": {
                      color: "#D32F2F",
                    },
                  }),
                },
                onClick: () => setOpenStartTimePicker(true),
              },
            }}
            timeSteps={{ minutes: 1 }}
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            label="End Time"
            value={endTimeValue}
            onChange={(newValue) => {
              setEndTimeError(false);
              dispatch(setEndTime(newValue));
              dispatch(timeChangedFunc(true));
            }}
            onOpen={() => setOpenEndTimePicker(true)}
            onClose={() => setOpenEndTimePicker(false)}
            open={openEndTimePicker}
            slotProps={{
              textField: {
                fullWidth: true,
                error: endTimeError || endFutureTimeError,
                helperText: endFutureTimeError
                  ? "End time cannot be in the future."
                  : endTimeError
                    ? "End time cannot be earlier than start time."
                    : "",
                sx: {
                  "& .MuiFormHelperText-root": {
                    marginLeft: 0,
                    marginRight: 0,
                  },
                  ...(endTimeError || endFutureTimeError
                    ? {
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor: "#D32F2F",
                          },
                        },
                        "& .MuiFormLabel-root.Mui-error": {
                          color: "#D32F2F",
                        },
                        "& .MuiFormHelperText-root.Mui-error": {
                          color: "#D32F2F",
                        },
                      }
                    : {}),
                },
                onClick: () => setOpenEndTimePicker(true),
              },
            }}
            timeSteps={{ minutes: 1 }}
          />
        </LocalizationProvider>
      </Box>

      <CustomSelectFilter
        menuItems={logValueOptions}
        changeSelectValueHandler={(e) =>
          dispatch(setLogTypeValue(e.target.value))
        }
        selectValue={logTypeValue}
        id="log_type"
        label="Log Type"
        minWidth="fullWidth"
        size="medium"
      />

      <CustomSelectFilter
        menuItems={programOptions}
        changeSelectValueHandler={(e) => {
          const selectedItem = programOptions?.find(
            (item) => item?.value === e.target.value
          );
          dispatch(setProgramKeyValue(selectedItem));
        }}
        selectValue={programKeyValue ? programKeyValue?.value : ""}
        id="program_key"
        label="Logging for"
        minWidth="fullWidth"
        size="medium"
      />

      <FormControlLabel
        sx={{ marginRight: "0", flex: "1" }}
        control={
          <Checkbox
            checked={communicationInteraction}
            onChange={(e) =>
              dispatch(setCommunicationInteraction(e.target.checked))
            }
          />
        }
        label="Interacted with the patient in this session."
      />

      <TextField
        multiline
        rows={6}
        label="Notes"
        value={notesValue}
        onChange={(e) => dispatch(setNotesValue(e.target.value))}
      />
    </Stack>
  );
};

export default EndTimeModalContent;
